/**
 * CoinW Exchange Metadata
 *
 * CoinW uses a Poloniex-style public REST API (`/api/v1/public?command=...`)
 * plus a token-gated socket.io / Engine.IO v3 WebSocket. The WS host is NOT a
 * fixed URL — it is issued by `GET /pusher/public-token` (e.g.
 * `wss://ws.futurescw.info`). The token expires quickly (~30s) and must be
 * refetched on every (re)connect.
 */

import type {
  ExchangeId,
  ExchangeMetadata,
  WsChannelDescriptor,
  WsTopologyInfo,
} from '../../lib/types';

const COINW_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: 'wss://ws.futurescw.info',
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 10_000,
  note: 'Token-gated socket.io / Engine.IO v3. Fetch a token via GET /pusher/public-token, then dial `${endpoint}/socket.io/?token=<token>&EIO=3&transport=websocket`. Subscribe with `42["subscribe", {"args": "<channel>:<BASE-QUOTE>"}]` (hyphen symbols). Reply `3` to engine.io `2` pings.',
};

const COINW_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: { channel: 'depth', topic: 'spot/level2', supportsBatching: false },
  ticker: { channel: 'ticker', topic: 'spot/market-api-ticker', supportsBatching: false },
  trade: { channel: 'trade', topic: 'spot/market-api-trade', supportsBatching: false },
};

export const COINW_METADATA: ExchangeMetadata = {
  id: 'coinw' as ExchangeId,
  restDocs: ['https://www.coinw.com/api-doc/'],
  wsDocs: ['https://www.coinw.com/api-doc/'],
  wsTopology: COINW_WS_TOPOLOGY,
  wsChannels: COINW_WS_CHANNELS,
  notes: [
    'REST symbol format: BASE_QUOTE underscore (e.g. XNK_USDT); WS uses BASE-QUOTE hyphen.',
    'Public market data: /api/v1/public?command=returnOrderBook|returnChartData|returnTicker|returnSymbol.',
    'No public REST trade history; recent trades arrive via the WS spot/market-api-trade channel.',
  ],
};
